import type { DealerConfigPatch, DealerConfigRow } from '@/lib/supabase/dealerConfig';

interface DealerSettings {
  dealer_id: string;
  is_included: boolean;
  notes: string;
  alias_names: string[];
}

/** Optimistic update: applies `patch` to the listed dealers (instant UI feedback). */
export function patchDealerRows<T extends DealerSettings>(
  dealers: T[],
  dealerIds: string[],
  patch: DealerConfigPatch
): T[] {
  const ids = new Set(dealerIds);
  return dealers.map(dealer => {
    if (!ids.has(dealer.dealer_id)) return dealer;
    return {
      ...dealer,
      ...(patch.isIncluded !== undefined && { is_included: patch.isIncluded }),
      ...(patch.notes !== undefined && { notes: patch.notes })
    };
  });
}

/** Replaces optimistic values with the state set_dealer_config returned. */
export function applyConfigRows<T extends DealerSettings>(
  dealers: T[],
  rows: DealerConfigRow[]
): T[] {
  const byId = new Map(rows.map(row => [row.dealer_id, row]));
  return dealers.map(dealer => {
    const row = byId.get(dealer.dealer_id);
    if (!row) return dealer;
    return {
      ...dealer,
      is_included: row.is_included,
      notes: row.notes ?? '',
      alias_names: row.alias_names.length ? row.alias_names : dealer.alias_names
    };
  });
}

/** Number of other dealers a write also reached (identical names). */
export const countAlsoAffected = (rows: DealerConfigRow[]) =>
  new Set(rows.flatMap(row => row.also_affects)).size;
